import React, { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import styled from 'styled-components';
import { Context } from '@components/Context';
import Colors from '@components/shared/Colors';
import { TEXT_STYLES, TextVariant, Typography } from '@components/shared/design/Typography';
import useFetch from '@components/useFetch';

interface Props {
  referralCode?: string;
  mobileOneRowBreakpoint?: number;
  buttonColor?: string;
  buttonTextColor?: string;
  fullWidth?: boolean;
  isModern?: boolean;
  borderRadius?: string;
}

export default function ReferralCodeCopy({
  referralCode,
  mobileOneRowBreakpoint,
  buttonColor,
  buttonTextColor,
  fullWidth,
  isModern,
  borderRadius
}: Props) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const refEl = useRef<HTMLDivElement>(null);
  const [finalReferralCode, setFinalReferralCode] = useState(referralCode);
  const { state } = useContext(Context);
  const { firebaseAuth } = state;
  const fetch = useFetch();

  useEffect(() => {
    if (firebaseAuth?.currentUser) {
      handleGetReferralCode();
    }
  }, [firebaseAuth?.currentUser]);

  async function handleGetReferralCode() {
    const url = `/recurly/get-referral-code`;

    if (finalReferralCode) {
      return;
    }

    try {
      const code = await fetch.get(url);
      setFinalReferralCode(code);
    } catch (err) {
      console.error('err', Error(JSON.stringify(err)));
    }
  }

  const copyToClipboard = () => {
    if (typeof window === 'undefined') return;
    const selection = window.getSelection();
    if (selection) {
      const range = document.createRange();
      const referralDiv: any = document.getElementById('referralToCopy');
      range.selectNode(referralDiv);
      if (!window) return;
      selection.removeAllRanges(); // clear current selection
      selection.addRange(range); // to select text
      document.execCommand('copy');

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!finalReferralCode) return null;

  return (
    <CodeWrap $fullWidth={!!fullWidth} $borderRadius={borderRadius}>
      <ReferralBox onClick={copyToClipboard} $mobileOneRowBreakpoint={mobileOneRowBreakpoint}>
        <CodeBox ref={refEl} id="referralToCopy" $isModern={isModern}>
          {isModern ? <Typography $variant={TextVariant.UpperText4}>{finalReferralCode}</Typography> : finalReferralCode}
        </CodeBox>
        <CopyButton $buttonColor={buttonColor} $buttonTextColor={buttonTextColor}>
          {t('components.copy-code')}
        </CopyButton>
      </ReferralBox>
      {copied && (
        <CopiedNotification>
          {' '}
          {t('components.code-copied')}
          <Tag />
        </CopiedNotification>
      )}
    </CodeWrap>
  );
}

const CodeWrap = styled.div<{ $fullWidth: boolean; $borderRadius?: string }>`
  position: relative;
  background-color: ${Colors.cream};
  border: 1px solid ${Colors.paleGold};
  border-radius: ${({ $borderRadius }) => $borderRadius || '2px'};
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const CopiedNotification = styled.div`
  position: absolute;
  top: -62px;
  left: 140px;
  transform: translateX(-50%);
  white-space: nowrap;
  padding: 15px;
  background: ${Colors.darkGreen};
  opacity: 1;
  color: ${Colors.cream};
  border-radius: 4px;
  font-family: 'BrownStd';
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  letter-spacing: 0;
  font-weight: 400;
`;

const Tag = styled.div`
  position: absolute;
  left: 50%;
  width: 15px;
  height: 15px;
  transform: rotate(45deg);
  background: ${Colors.darkGreen};
  bottom: -7px;
`;

const ReferralBox = styled.div<{ $mobileOneRowBreakpoint?: number }>`
  margin: 0;
  display: flex;
  justify-content: center;

  @media only screen and (max-width: ${({ $mobileOneRowBreakpoint }) =>
      $mobileOneRowBreakpoint || $mobileOneRowBreakpoint === 0 ? $mobileOneRowBreakpoint : 768}px) {
    flex-direction: column;
    width: 100%;
  }
`;

const CodeBox = styled.div<{ $isModern?: boolean }>`
  ${TEXT_STYLES[TextVariant.UpperText3]};
  text-align: center;
  line-height: 44px;
  text-transform: uppercase;
  padding: ${({ $isModern }) => ($isModern ? '13px 15px' : '0 26px 0 15px')};
  background: ${({ $isModern }) => ($isModern ? Colors.cream : 'transparent')};
  flex-grow: 1;
  width: auto;
  white-space: nowrap;
  flex: 1;

  @media (max-width: 700px) {
    ${TEXT_STYLES[TextVariant.UpperText5]};
    line-height: 44px;
  }
`;

const CopyButton = styled.div<{ $buttonColor?: string; $buttonTextColor?: string }>`
  font-family: 'BrownStd';
  font-size: 12px;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 1px;
  text-align: center;
  text-transform: uppercase;
  padding: 0 25px;
  line-height: 44px;
  color: ${(props) => (props.$buttonTextColor ? props.$buttonTextColor : Colors.navyBlue)};
  background: ${(props) => (props.$buttonColor ? props.$buttonColor : Colors.paleGold)};
  cursor: pointer;
  white-space: nowrap;
  flex: 1;

  &:hover {
    color: ${Colors.paleGold};
    background-color: ${Colors.darkGreen};
  }
`;
